import { adPixels } from '../../services/adPixels';
import React, { useState, useEffect } from 'react';
import { ViewTab } from '../../types';
import { useAuth } from '../../auth/AuthContext';

interface PublicHeaderProps {
  onNavigate: (tab: ViewTab | string) => void;
  activeTab?: 'landing' | 'pricing' | 'partenariat' | 'legal' | 'blog';
}

export const PublicHeader: React.FC<PublicHeaderProps> = ({ onNavigate, activeTab = 'landing' }) => {
  const { isAuthenticated } = useAuth() as any;
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const mainEl = document.querySelector('main');
    if (mainEl) mainEl.scrollTop = 0;
    setIsScrolled(false);
  }, [activeTab]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = (e?: Event) => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const target = e?.target as HTMLElement | Document | null;
          const mainEl = document.querySelector('main');
          const elementScroll = (target instanceof HTMLElement ? target.scrollTop : 0) || (mainEl ? mainEl.scrollTop : 0);
          const windowScroll = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
          const scrollY = Math.max(windowScroll, elementScroll);
          setIsScrolled((prev) => {
            // Hystérésis anti-saccade : active à 30px, ne désactive qu'en dessous de 12px
            if (scrollY > 30) return true;
            if (scrollY < 12) return false;
            return prev;
          });
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true } as any);
      document.removeEventListener('scroll', handleScroll, { capture: true } as any);
    };
  }, []);

  // Sur la page vitrine (landing), le header débute transparent au-dessus du canvas interactif
  // Sur les autres pages publiques (tarifs, partenariat, blog, mentions légales), le header flottant satiné est actif en continu
  const isFloating = isScrolled || activeTab !== 'landing';

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 pointer-events-none flex justify-center pt-[env(safe-area-inset-top)] px-3 sm:px-4 md:px-6"
    >
      <div
        className={`pointer-events-auto transition-all duration-300 ease-out flex items-center justify-between ${
          isFloating
            ? 'w-full max-w-3xl md:max-w-4xl lg:max-w-5xl mt-2 sm:mt-3 px-3.5 sm:px-5 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-slate-950/85 via-slate-900/50 to-slate-800/20 backdrop-blur-2xl border border-white/15 border-t-white/25 shadow-xl shadow-black/15 text-white'
            : 'w-full max-w-6xl xl:max-w-7xl h-[56px] sm:h-16 md:h-[68px] px-1 sm:px-2 bg-transparent border border-transparent shadow-none text-slate-900'
        }`}
      >
        {/* Logo */}
        <button
          onClick={() => onNavigate('landing' as ViewTab)}
          className="flex items-center cursor-pointer shrink-0 group focus:outline-none py-0.5"
        >
          <img
            src="/logo.webp"
            alt="Zeyilo"
            width={160}
            height={48}
            fetchPriority="high"
            decoding="async"
            className={`w-auto object-contain shrink-0 transition-all duration-300 group-hover:scale-[1.03] ${
              isFloating ? 'h-7 sm:h-8 md:h-8.5 brightness-110' : 'h-8.5 sm:h-10 md:h-11'
            }`}
          />
        </button>

        {/* Actions & Navigation à droite */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Navigation discrète sur écran sm+ */}
          <div className="hidden sm:flex items-center gap-1">
            {activeTab !== 'landing' && (
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate('landing' as ViewTab);
                }}
                className={`text-xs sm:text-sm font-medium px-2.5 sm:px-3 py-1 rounded-full transition-colors cursor-pointer ${
                  isFloating
                    ? 'text-slate-300 hover:text-white hover:bg-white/10'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
                }`}
              >
                Accueil
              </a>
            )}

            {activeTab !== 'pricing' && (
              <a
                href="/pricing"
                onMouseEnter={() => { import('../../views/PricingView').catch(() => {}); }}
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate('pricing' as ViewTab);
                }}
                className={`text-xs sm:text-sm font-medium px-2.5 sm:px-3 py-1 rounded-full transition-colors cursor-pointer ${
                  isFloating
                    ? 'text-slate-300 hover:text-white hover:bg-white/10'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
                }`}
              >
                Tarifs
              </a>
            )}
          </div>

          {!isAuthenticated ? (
            <>
              {/* Bouton 1 : Connexion */}
              <a
                href="/login"
                onMouseEnter={() => { import('../../auth/LoginPage').catch(() => {}); }}
                onFocus={() => { import('../../auth/LoginPage').catch(() => {}); }}
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate('login');
                }}
                className={`text-xs sm:text-sm font-medium px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full transition-all active:scale-95 cursor-pointer whitespace-nowrap shrink-0 ${
                  isFloating
                    ? 'text-slate-200 hover:text-white hover:bg-white/10 active:bg-white/20'
                    : 'text-slate-700 hover:text-slate-950 hover:bg-slate-100/80 active:bg-slate-200'
                }`}
              >
                Connexion
              </a>

              {/* Bouton 2 : Démarrer gratuitement (Minimaliste & compact) */}
              <a
                href="/register"
                onMouseEnter={() => { import('../../auth/RegisterPage').catch(() => {}); }}
                onFocus={() => { import('../../auth/RegisterPage').catch(() => {}); }}
                onClick={(e) => {
                  e.preventDefault();
                  adPixels.track('Lead');
                  onNavigate('register' as ViewTab);
                }}
                className={`text-xs sm:text-sm font-medium px-3 sm:px-3.5 py-1 sm:py-1.5 rounded-full cursor-pointer transition-all active:scale-95 shadow-xs whitespace-nowrap shrink-0 flex items-center gap-1.5 group ${
                  isFloating
                    ? 'bg-white hover:bg-slate-100 text-slate-950 shadow-md shadow-black/20'
                    : 'bg-slate-900 hover:bg-black text-white'
                }`}
              >
                <span className="hidden sm:inline">Démarrer gratuitement</span>
                <span className="sm:hidden">Offre gratuite</span>
                <span
                  className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] font-bold transition-colors ${
                    isFloating
                      ? 'bg-slate-950/10 text-slate-950 group-hover:bg-slate-950/20'
                      : 'bg-white/20 text-white group-hover:bg-white/30'
                  }`}
                >
                  ↗
                </span>
              </a>
            </>
          ) : (
            <a
              href="/dashboard"
              onMouseEnter={() => { import('../../App').catch(() => {}); }}
              onFocus={() => { import('../../App').catch(() => {}); }}
              onClick={(e) => {
                e.preventDefault();
                onNavigate('dashboard' as ViewTab);
              }}
              className={`text-xs sm:text-sm font-medium px-3 sm:px-3.5 py-1 sm:py-1.5 rounded-full cursor-pointer transition-all active:scale-95 shadow-xs whitespace-nowrap flex items-center gap-1.5 shrink-0 group ${
                isFloating
                  ? 'bg-white hover:bg-slate-100 text-slate-950 shadow-md shadow-black/20'
                  : 'bg-slate-900 hover:bg-black text-white'
              }`}
            >
              <span>Tableau de bord</span>
              <span
                className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[9px] font-bold transition-colors ${
                  isFloating
                    ? 'bg-slate-950/10 text-slate-950 group-hover:bg-slate-950/20'
                    : 'bg-white/20 text-white group-hover:bg-white/30'
                }`}
              >
                ↗
              </span>
            </a>
          )}
        </div>
      </div>
    </header>
  );
};
